<template>
  <!-- Dialog Box for Attachment Viewer -->
  <v-dialog v-model="isDialogOpen" max-width="700" persistent>
    <v-card class="dialog-card">
      <v-card-title class="font-weight-medium secondary--text primary">
        Attachment Viewer
        <v-spacer></v-spacer>
        <v-btn icon large @click="$emit('closeDialog')">
          <v-icon small color="secondary">mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text class="mt-5">
        <!-- Render Image -->

        <div v-if="dialogData.type === 'image'" class="text-center">
          <div class="mb-2">
            <v-btn icon @click="zoomIn">
              <v-icon>mdi-magnify-plus</v-icon>
            </v-btn>
            <v-btn icon @click="zoomOut">
              <v-icon>mdi-magnify-minus</v-icon>
            </v-btn>
            <v-btn class="font-weight-light" icon>
              <v-icon color="#707070" @click.stop.prevent="downloadImage"
                >mdi-download</v-icon
              >
            </v-btn>
            <v-btn icon @click="printImage()">
              <v-icon>mdi-printer</v-icon>
            </v-btn>
          </div>

          <div
            v-if="dialogData.type === 'image'"
            class="image-wrapper"
            style="overflow: auto; max-height: 80vh"
          >
            <img
              :src="imageUrl"
              alt="Attachment"
              :style="{
                transform: `scale(${zoom})`,
                transformOrigin: 'center',
              }"
              class="attachment-image"
            />
          </div>
        </div>

        <!-- Render PDF -->
        <div v-else-if="dialogData.type === 'application'">
          <iframe
            :src="pdfUrl"
            class="attachment-iframe"
            frameborder="0"
          ></iframe>
        </div>

        <!-- Render CSV (Text Preview) -->
        <div v-else-if="dialogData.type === 'csv'">
          <pre>{{ atob(dialogData.file_binary) }}</pre>
        </div>

        <!-- Unsupported File -->
        <div v-else>
          <p>Unsupported file type</p>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  data() {
    return {
      isDialogOpen: false,
      dialogData: {},
      zoom: 1,
      pdfUrl: null,
      imageUrl: null,
    };
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
  },
  computed: {},
  methods: {
    zoomIn() {
      this.zoom += 0.1;
    },
    zoomOut() {
      this.zoom = Math.max(0.1, this.zoom - 0.1);
    },
    printImage() {
      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      document.body.appendChild(iframe);

      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!doc) return;

      const imageHtml = `
    <html>
      <head>
        <title>Print Image</title>
        <style>
          body, html {
            margin: 0;
            padding: 0;
          }
          img {
            width: 100%;
            max-width: 100%;
          }
        </style>
      </head>
      <body>
        <img src="${this.imageUrl}" onload="window.focus(); window.print(); window.onafterprint = () => { window.close(); }" />
      </body>
    </html>
  `;

      doc.open();
      doc.write(imageHtml);
      doc.close();
    },
    downloadImage() {
      const a = document.createElement("a");
      a.href = this.imageUrl;
      a.download = this.dialogData.filename || "student_document.jpg";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    },
  },
  created() {
    this.dialogData = { ...this.value };

    const byteCharacters = atob(this.dialogData.file_binary);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);

    // Dynamically decide MIME type
    let mimeType = "application/pdf";
    if (this.dialogData.type === "image") {
      mimeType = "image/png";
    }
    const blob = new Blob([byteArray], { type: mimeType });
    const url = URL.createObjectURL(blob);

    if (mimeType.startsWith("application/pdf")) {
      this.pdfUrl = url; // For <iframe> or <object> rendering
    } else {
      this.imageUrl = url; // For <img> tag rendering
    }

    this.isDialogOpen = true;
  },
};
</script>

<style scoped>
.dialog-card {
  border-radius: 16px;
  overflow: hidden;
}
.attachment-image {
  width: 100%;
  height: auto;
  max-height: 500px;
  object-fit: contain;
}

.attachment-iframe {
  width: 100%;
  height: 500px;
  border-radius: 8px;
}
</style>
